/**
 * Centralized Frontend Error Handling Utility
 *
 * Separates internal developer debugging from safe, clean user-facing UI messages.
 */

// Indicators of technical stack traces / internal server errors
const TECHNICAL_ERROR_PATTERNS = [
  /Unhandled Exception/i,
  /PrismaClient/i,
  /prisma\./i,
  /Invocation in/i,
  /Argument `.*?` must not be null/i,
  /at\s+.*?\:\d+\:\d+/i,
  /node_modules/i,
  /TypeError\:/i,
  /ReferenceError\:/i,
  /SyntaxError\:/i,
  /AxiosError\:/i,
  /\[object Object\]/i,
];

/**
 * Checks if a string message looks like an internal technical stack trace or sensitive code dump.
 */
export const isTechnicalError = (msg) => {
  if (typeof msg !== 'string') return false;
  return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(msg));
};

/**
 * Parses an API error (Axios error, RTK Query error, Fetch error, or generic Error)
 * returns a safe, user-facing string suitable for toasts or UI error alerts.
 *
 * @param {any} error - The caught error object
 * @param {string | { context?: string, fallbackMsg?: string, t?: Function }} [optionsOrFallback] - Optional options or fallback message
 * @returns {string} - Clean, production-safe error message
 */
export const parseApiError = (error, optionsOrFallback = 'Something went wrong. Please try again.') => {
  const options =
    typeof optionsOrFallback === 'string'
      ? { fallbackMsg: optionsOrFallback }
      : (optionsOrFallback || {});

  const { context, fallbackMsg = 'Something went wrong. Please try again.', t } = options;

  // Extract status & response data across Axios, RTK Query, and Fetch
  const status = error?.response?.status || error?.status;
  const resData = error?.response?.data || error?.data;
  const rawMsg = resData?.message || error?.message;
  const errorId = resData?.errorId;
  const errorCode = resData?.code;
  const requestUrl = error?.config?.url || error?.response?.config?.url || '';

  const isLoginContext =
    context === 'login' ||
    errorCode === 'INVALID_CREDENTIALS' ||
    requestUrl.includes('/auth/login');

  // Development logger: Always log full error context for developers
  if (
    process.env.NODE_ENV !== 'production' ||
    (typeof window !== 'undefined' && window.localStorage?.getItem('debug_errors') === 'true')
  ) {
    console.error('[API Error Detail]', {
      status,
      endpoint: requestUrl,
      method: error?.config?.method || error?.response?.config?.method,
      errorId,
      errorCode,
      resData,
      rawError: error,
    });
  }

  // 1. Network / Offline / Connection errors
  if (
    status === 'FETCH_ERROR' ||
    status === 'TIMEOUT_ERROR' ||
    error?.code === 'ERR_NETWORK' ||
    error?.message === 'Network Error' ||
    (error?.name === 'TypeError' && typeof rawMsg === 'string' && rawMsg.includes('Failed to fetch'))
  ) {
    return t
      ? t('signIn.errors.networkError', 'Unable to connect to the server. Please check your internet connection.')
      : 'Unable to connect to the server. Please check your internet connection.';
  }

  // 2. Server Error (500+)
  if (typeof status === 'number' && status >= 500) {
    const genericServerMsg = t
      ? t('signIn.errors.serverError', 'Something went wrong on our server. Please try again in a few moments.')
      : 'Something went wrong on our server. Please try again in a few moments.';
    return errorId ? `${genericServerMsg} (Ref: ${errorId})` : genericServerMsg;
  }

  // 3. Status 403 (Forbidden - Account Blocked or Access Denied)
  if (status === 403) {
    if (errorCode === 'ACCOUNT_PERMANENTLY_BLOCKED') {
      if (typeof rawMsg === 'string' && !isTechnicalError(rawMsg) && rawMsg !== 'Forbidden') {
        return rawMsg;
      }
      return t
        ? t('signIn.errors.accountPermanentlyBlocked', 'This account has been permanently locked for security reasons after repeated failed login attempts. Please contact support via email.')
        : 'This account has been permanently locked for security reasons after repeated failed login attempts. Please contact support via email.';
    }

    const isBlocked =
      errorCode === 'ACCOUNT_BLOCKED' ||
      (typeof rawMsg === 'string' &&
        (rawMsg.toLowerCase().includes('suspendu') || rawMsg.toLowerCase().includes('bloqu')));

    if (isBlocked) {
      if (typeof rawMsg === 'string' && !isTechnicalError(rawMsg) && rawMsg !== 'Forbidden') {
        return rawMsg;
      }
      return t
        ? t('signIn.errors.accountBlocked', 'This account has been suspended by an administrator. Please contact support.')
        : 'This account has been suspended by an administrator. Please contact support.';
    }

    if (typeof rawMsg === 'string' && !isTechnicalError(rawMsg) && rawMsg !== 'Forbidden') {
      return rawMsg;
    }
    return "You don't have permission to perform this action.";
  }

  // 4. Status 401 (Unauthorized)
  if (status === 401) {
    if (isLoginContext) {
      if (
        typeof rawMsg === 'string' &&
        rawMsg.trim() !== '' &&
        !isTechnicalError(rawMsg) &&
        rawMsg !== 'Unauthorized'
      ) {
        return rawMsg;
      }
      return t
        ? t('signIn.errors.invalidLogin', 'Invalid username or password')
        : 'Invalid username or password';
    }

    // In a non-login authenticated context, 401 means the token/session actually expired
    return t
      ? t('signIn.errors.sessionExpired', 'Your session has expired. Please log in again.')
      : 'Your session has expired. Please log in again.';
  }

  // 5. Status 429 (Rate Limit / Too Many Requests / Temporary Lockout)
  if (status === 429) {
    if (errorCode === 'ACCOUNT_TEMPORARILY_BLOCKED') {
      if (typeof rawMsg === 'string' && !isTechnicalError(rawMsg)) {
        return rawMsg;
      }
      return t
        ? t('signIn.errors.accountTemporarilyBlocked', 'This account is temporarily locked after multiple failed login attempts.')
        : 'This account is temporarily locked after multiple failed login attempts.';
    }

    if (typeof rawMsg === 'string' && !isTechnicalError(rawMsg) && rawMsg !== 'Too Many Requests') {
      return rawMsg;
    }

    return t
      ? t('signIn.errors.tooManyRequests', 'Too many attempts. Please wait a few moments before trying again.')
      : 'Too many attempts. Please wait a few moments before trying again.';
  }

  // 6. Handle array of validation error messages (e.g. NestJS ValidationPipe)
  if (Array.isArray(rawMsg)) {
    const cleanMsgs = rawMsg.filter((m) => typeof m === 'string' && !isTechnicalError(m));
    if (cleanMsgs.length > 0) {
      return cleanMsgs.join(', ');
    }
  }

  // 7. Handle single string message if safe and non-technical
  if (typeof rawMsg === 'string' && rawMsg.trim() !== '' && !isTechnicalError(rawMsg)) {
    return rawMsg;
  }

  // 8. Status 404 (Not Found)
  if (status === 404) {
    return 'The requested resource was not found.';
  }

  // Fallback with errorId correlation if present
  if (errorId) {
    return `${fallbackMsg} (Ref: ${errorId})`;
  }

  return fallbackMsg;
};

/**
 * Extracts lockout details from API error responses for authentication flows
 */
export const extractLockoutInfo = (error) => {
  const status = error?.response?.status || error?.status;
  const resData = error?.response?.data || error?.data;
  const errorCode = resData?.code;

  const isTemporarilyLocked = status === 429 && errorCode === 'ACCOUNT_TEMPORARILY_BLOCKED';
  const isPermanentlyLocked = status === 403 && errorCode === 'ACCOUNT_PERMANENTLY_BLOCKED';

  let remainingSeconds = typeof resData?.remainingSeconds === 'number' && resData.remainingSeconds > 0
    ? resData.remainingSeconds
    : 0;

  const lockoutExpiresAt = resData?.lockoutExpiresAt || null;

  // Fallback 1: Calculate remaining seconds from lockoutExpiresAt timestamp
  if ((!remainingSeconds || remainingSeconds <= 0) && lockoutExpiresAt) {
    const diff = Math.ceil((new Date(lockoutExpiresAt).getTime() - Date.now()) / 1000);
    if (diff > 0) {
      remainingSeconds = diff;
    }
  }

  // Fallback 2: If temporary block is flagged but duration was missing, default to 15 minutes (900s)
  if (isTemporarilyLocked && (!remainingSeconds || remainingSeconds <= 0)) {
    remainingSeconds = 15 * 60;
  }

  const stage = typeof resData?.stage === 'number' ? resData.stage : (isTemporarilyLocked ? 1 : 0);
  const remainingAttempts = typeof resData?.remainingAttempts === 'number' ? resData.remainingAttempts : null;
  const supportEmail = resData?.supportEmail || 'support@billswift.cloud';

  return {
    isTemporarilyLocked,
    isPermanentlyLocked,
    remainingSeconds,
    lockoutExpiresAt,
    stage,
    remainingAttempts,
    supportEmail,
  };
};

export default parseApiError;
